import { useMemo, useState, useCallback } from 'react'
import { logger } from '@/utils/logger';
import { ComponentForm } from '../components-mgmt/ComponentForm'
import { useCreateComponent, useUpdateComponent, useComponents } from '../../hooks/useComponents'
import { useProject } from '../../contexts/ProjectContext'
import { useFocusTrap } from '../interlock/useFocusTrap'
import type { Component, CreateComponentDTO } from '@fmeastudio/shared'
import './CreateComponentModal.css'

interface CreateComponentModalProps {
  component?: Component
  onClose: () => void
}

export function CreateComponentModal({ component, onClose }: CreateComponentModalProps) {
  const { activeProject } = useProject()
  // Accessible modal semantics + focus management, same shared primitive as
  // Interlock's RiskIntakeModal (useFocusTrap.ts): focus moves into the
  // dialog on open, Tab is trapped, Escape closes, focus returns to the
  // trigger on close.
  const panelRef = useFocusTrap<HTMLDivElement>(true, onClose)
  const createMutation = useCreateComponent()
  const updateMutation = useUpdateComponent()
  // sow-375: Track form validity for submit button state
  const [isFormValid, setIsFormValid] = useState(!!component) // Existing components are valid

  // SoW-311 Fix: Get fresh component data from React Query cache
  // This ensures we have the latest diagramWidth/diagramHeight after canvas resize
  const { data: allComponents = [] } = useComponents()
  const freshComponent = useMemo(() => {
    if (!component?.id) return component
    return allComponents.find(c => c.id === component.id) || component
  }, [component, allComponents])

  // sow-375: Handle validity change from ComponentForm
  const handleValidityChange = useCallback((valid: boolean) => {
    setIsFormValid(valid)
  }, [])

  const handleSubmit = async (data: CreateComponentDTO) => {
    if (!activeProject && !freshComponent) {
      return
    }

    try {
      if (freshComponent) {
        // Update existing component
        await updateMutation.mutateAsync({
          id: freshComponent.id,
          data
        })
      } else {
        // Create new component
        await createMutation.mutateAsync(data)
      }
      onClose()
    } catch (error) {
      logger.error('Failed to save component', error, { module: 'CreateComponentModal' });
      throw error // Let ComponentForm handle the error
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <div className="modal-overlay" data-testid="component-modal-overlay">
      <div
        ref={panelRef}
        className="modal-content"
        data-testid="component-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-component-modal-title"
      >
        <div className="modal-header">
          <h2 id="create-component-modal-title">
            {freshComponent ? 'Edit Component' : 'Create Component'}
          </h2>
          <div className="modal-header-actions">
            <button
              type="submit"
              form="component-form"
              className="btn-primary"
              disabled={isLoading || !isFormValid}
              title={!isFormValid ? 'Fill in all required fields' : ''}
            >
              {isLoading ? 'Saving...' : freshComponent ? 'Update' : 'Create'}
            </button>
            <button
              className="modal-close"
              onClick={onClose}
              data-testid="modal-close-btn"
              aria-label="Close dialog"
            >
              ×
            </button>
          </div>
        </div>

        <div className="modal-body">
          <ComponentForm
            component={freshComponent}
            onSubmit={handleSubmit}
            onCancel={onClose}
            loading={isLoading}
            hideActions
            onValidityChange={handleValidityChange}
          />
        </div>
      </div>
    </div>
  )
}
